import React, { useEffect, useRef } from 'react';
import { RegistrationHandler } from 'router/RegistrationHandler/RegistrationHandler';
import { MainLayout } from 'components/MainLayout/MainLayout';
import { theme } from 'theme/theme';
import { Logger } from 'common/logger';
import { AuthenticationRoutes } from 'common/auth/AuthenticationRoutes';
import { globalStyle } from 'theme/global';
import { Global, ThemeProvider } from '@emotion/react';
import { CSSReset, ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import { chakraTheme } from 'theme/chakra/chakraTheme';
import { useRematchSelector } from 'models/rematchStore';
import {
    SCREEN_OFFSHORE_ACTIVITY,
    SCREEN_OPERATIONS,
    SCREEN_OPERATIONS_COMMERCIAL,
    SCREEN_REPORTS,
    SCREEN_STATUS,
} from 'common/constants';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { authXConfig } from 'router/authx-config';

export function Root(props) {
    const { dispatch, children } = props;
    const location = useLocation();
    const { pathname, search } = location;

    const { id } = useRematchSelector((state) => state.account.user);
    const token = localStorage.getItem('access_token');

    const fetchOnceRef = useRef(false);

    /* ✅ 1. Redirect to Cognito if token is missing */
    useEffect(() => {
        // add a check to prevent this api call if url contain /callback as that means user is coming back from cognito and token is not yet set in local storage. This is to prevent infinite loop of redirects to cognito until token is set in local storage.

        if (!token && !window.location.pathname.toLowerCase().includes('/authorization/callback')) {
            Logger.warn('No token found, redirecting to Cognito');
            window.location.replace(authXConfig.API.CognitoAPI.authURL);
        }
    }, [token]);

    /* ✅ 2. Fetch user ONLY ONCE if token exists but user not loaded */
    useEffect(() => {
        //if we directly hit the logout url, do not fetch user details. It should logout directly
        if (window.location.pathname.toLowerCase().includes('/logout')) {
            return;
        }
        if (!token) return;
        if (id) return;
        if (fetchOnceRef.current) return;

        fetchOnceRef.current = true;

        (async () => {
            try {
                Logger.info('Fetching user details...');

                const res = await axiosInstance.get(authXConfig.API.CognitoAPI.userURL);
                dispatch({
                    type: 'account/tokenChanged',
                    payload: { token },
                });
                dispatch.account.setUserData({ user: res.data });
                dispatch.account.setIsLoggedIn(true);
                dispatch({ type: 'appData/filterParkListByAuth' });

                // dispatch({
                //     type: 'account/authorizeUser',
                //     payload: { authUser: res.data },
                // });

                // dispatch({
                //     type: 'account/tokenChanged',
                //     payload: {
                //         token: authUser.access_token,
                //     },
                // });
                // dispatch({ type: 'account/authorizeUser', payload: { authUser } });

                Logger.info('User loaded successfully');
            } catch (err) {
                Logger.error('User fetch failed', err);

                if (err.response?.status === 401) {
                    localStorage.removeItem('access_token');
                    window.location.replace(authXConfig.API.CognitoAPI.authURL);
                }
            }
        })();
    }, [token, id, dispatch]);

    /* ✅ 3. Existing navigation logic (unchanged) */
    useEffect(() => {
        dispatch.routing.setLocation({ pathname, search });
        dispatch({ type: 'appData/setModelError', modelError: null });

        const parts = pathname.split('/');
        const selectedScreen = parts[2]?.toUpperCase();

        if (
            [SCREEN_OPERATIONS, SCREEN_REPORTS, SCREEN_OFFSHORE_ACTIVITY, SCREEN_OPERATIONS_COMMERCIAL].includes(
                selectedScreen
            )
        ) {
            dispatch({
                type: 'appData/checkIfScreenChanged',
                payload: selectedScreen,
            });

            dispatch({
                type: 'appData/setIsWebSocketEnabled',
                payload: [SCREEN_OPERATIONS, SCREEN_STATUS].includes(selectedScreen),
            });

            const selectedPark = parts[1]?.toUpperCase();
            if (selectedPark && selectedPark !== 'LOGOUT') {
                dispatch({
                    type: 'appData/checkIfParkChanged',
                    payload: selectedPark,
                });
            }
        }
    }, [location, dispatch]);

    /* ✅ 4. Fetch app config only after user is loaded */
    useEffect(() => {
        if (id) {
            dispatch({ type: 'appData/fetchAppConfig' });
        }
    }, [id, dispatch]);

    return (
        <RegistrationHandler location={location}>
            <Global styles={globalStyle} />
            <CSSReset />
            <ColorModeScript initialColorMode={chakraTheme.config.initialColorMode} />
            <ThemeProvider theme={theme}>
                <ChakraProvider theme={chakraTheme}>
                    <AuthenticationRoutes>
                        <MainLayout>{children}</MainLayout>
                    </AuthenticationRoutes>
                </ChakraProvider>
            </ThemeProvider>
        </RegistrationHandler>
    );
}
